// 監控範本的前端 mock（只在 KMS 操作台 mock = `all` 時接，../mockMode.ts）：kmgr 沒部署的環境看畫面用；狀態只放記憶體。
// 預覽：每一項都當成「新增」，第一個家戶的「交換器 / 路由器」當成客戶改過（預設保留）；套用：1 秒後全部套用完成。形狀照 kmgr M5-02。
import { KinioError } from '@kinio/web-sdk';
import type { MockWorld } from '../mock';
import type { HomeTemplate, Preview, PreviewRow, Template, TemplateBody, TemplateItem, TemplateJob } from './types';

interface MockTpl {
  t: Template;
  versions: TemplateBody[];
}

let tpls: MockTpl[] = [];
let jobs: (TemplateJob & { startMs: number })[] = [];
const applied: Record<string, { template_id: string; version: number }> = {};
let seq = 0;

const iso = (ms: number) => new Date(ms).toISOString();

/** 這個路徑歸範本的 mock 管（../api.ts kmgrFetch 的 mock 分派）。 */
export function isTemplateMockPath(path: string): boolean {
  return /^\/v1\/orgs\/[^/]+\/kmgr\/templates(\/|$)/.test(path) || /^\/v1\/homes\/[^/]+\/kmgr\/template$/.test(path);
}

export function resetTemplateMock(): void {
  tpls = [];
  jobs = [];
  seq = 0;
}

function itemsOf(b: TemplateBody): TemplateItem[] {
  const out: TemplateItem[] = [];
  if (b.severity_overrides) out.push('severity_overrides');
  if (b.maintenance) out.push('maintenance');
  if (b.business_hours) out.push('business_hours');
  if (b.notify) out.push('notify');
  if (b.probes?.length) out.push('probes');
  if (b.auto_rules?.length) out.push('auto_rules');
  return out;
}

function rowsFor(b: TemplateBody, first: boolean, crossOrg: boolean): PreviewRow[] {
  const rows: PreviewRow[] = [];
  if (b.severity_overrides) {
    for (const r of b.severity_overrides.rules) {
      const ov = first && r.key === 'network_device';
      rows.push({
        item: 'severity_overrides',
        key: r.key,
        before: { severity: ov ? 'P3' : 'P1', debounce_min: 3 },
        after: { severity: r.severity, debounce_min: r.debounce_min },
        change: 'update',
        default: ov ? 'keep' : 'overwrite',
        local_override: ov ? { by: 'u-mock-customer', by_org: 'org-mock-customer', at: iso(Date.now() - 86_400_000), side: 'home_org' } : undefined,
      });
    }
  }
  if (b.maintenance) rows.push({ item: 'maintenance', key: 'schedule', before: null, after: b.maintenance, change: 'add', default: 'overwrite' });
  if (b.business_hours) rows.push({ item: 'business_hours', key: 'windows', before: null, after: b.business_hours, change: 'add', default: 'overwrite' });
  if (b.notify) {
    rows.push({ item: 'notify', key: 'schedule_id', before: null, after: b.notify.schedule_id, change: 'add', default: 'overwrite' });
    rows.push({ item: 'notify', key: 'customer_contact', before: null, after: null, change: 'same', default: 'keep', customer_field: true });
  }
  for (const p of b.probes ?? []) rows.push({ item: 'probes', key: p.key, before: null, after: [{ target: 'srv-01', kind: p.kind, port: p.port, interval_s: p.interval_s ?? 60 }], change: 'add', default: 'overwrite' });
  for (const r of b.auto_rules ?? [])
    rows.push({ item: 'auto_rules', key: r.key, before: null, after: { service: r.target_match.service, checks: r.limits.checks ?? 3, targets: ['KMA-MOCK-1'] }, change: 'add', default: 'overwrite', pending_rule: crossOrg });
  return rows;
}

const notFound = () => new KinioError(404, 'NOT_FOUND', '找不到');

export const templateMock = {
  async handle(w: MockWorld, method: string, path: string, query: Record<string, string | number | string[] | undefined>, body: unknown): Promise<unknown> {
    const hm = /^\/v1\/homes\/([^/]+)\/kmgr\/template$/.exec(path);
    if (hm) {
      const home = decodeURIComponent(hm[1]);
      const a = applied[home];
      const tp = a && tpls.find((x) => x.t.template_id === a.template_id);
      const out: HomeTemplate = { home_id: home, template: null };
      if (tp)
        out.template = {
          template_id: tp.t.template_id,
          name: tp.t.name,
          template_org_id: tp.t.org_id,
          version: a.version,
          body: tp.versions[a.version - 1],
          local_overrides: [],
          archived: tp.t.archived,
          newer_version: tp.t.current_version > a.version ? tp.t.current_version : undefined,
        };
      return out;
    }
    const m = /^\/v1\/orgs\/([^/]+)\/kmgr\/templates(?:\/([^/]+))?(?:\/(versions|apply|apply-jobs)(?:\/([^/]+))?(?:\/(cancel|retry))?)?$/.exec(path);
    if (!m) throw notFound();
    const [, orgEnc, id, sub, subId, verb] = m;
    const org = decodeURIComponent(orgEnc);
    const now = Date.now();
    if (!id) {
      if (method === 'GET') return { org_id: org, templates: tpls.filter((x) => x.t.org_id === org && (query.archived === '1' || !x.t.archived)).map((x) => x.t) };
      const b = body as { name: string; description?: string; body: TemplateBody };
      const t: Template = {
        template_id: `ktp-MOCK${++seq}`,
        org_id: org,
        name: b.name,
        description: b.description ?? '',
        current_version: 1,
        items: itemsOf(b.body),
        archived: false,
        created_by: w.me,
        updated_by: w.me,
        created_at: iso(now),
        updated_at: iso(now),
        body: b.body,
        applied_homes: 0,
        jobs: [],
      };
      tpls.push({ t, versions: [b.body] });
      return t;
    }
    const tp = tpls.find((x) => x.t.template_id === decodeURIComponent(id) && x.t.org_id === org);
    if (!tp) throw notFound();
    const refresh = () => {
      for (const j of jobs) {
        if (j.state !== 'running' || now - j.startMs <= 1000) continue;
        Object.assign(j, { state: 'done', finished_at: iso(now), counts: { pending: 0, applied: j.total, skipped: 0, failed: 0 } });
        for (const h of j.homes) h.result = 'applied';
      }
    };
    refresh();
    const summaries = () => jobs.filter((j) => j.template_id === tp.t.template_id).reverse();
    if (!sub) {
      if (method === 'GET') return { ...tp.t, body: tp.versions[tp.t.current_version - 1], jobs: summaries().slice(0, 5) };
      if (method === 'DELETE') {
        tp.t.archived = true;
        return undefined;
      }
      const b = body as { name?: string; description?: string; body?: TemplateBody };
      if (b.name !== undefined) tp.t.name = b.name;
      if (b.description !== undefined) tp.t.description = b.description;
      if (b.body && JSON.stringify(b.body) !== JSON.stringify(tp.versions[tp.t.current_version - 1])) {
        tp.versions.push(b.body);
        tp.t.current_version++;
        tp.t.items = itemsOf(b.body);
      }
      tp.t.updated_at = iso(now);
      return { ...tp.t, body: tp.versions[tp.t.current_version - 1] };
    }
    if (sub === 'versions') return { template_id: tp.t.template_id, versions: tp.versions.map((_, i) => ({ version: i + 1, items: itemsOf(tp.versions[i]), created_by: w.me, created_at: tp.t.created_at })).reverse() };
    if (sub === 'apply' && subId === 'preview') {
      const b = body as { home_ids: string[]; version?: number };
      const v = b.version || tp.t.current_version;
      const homes = b.home_ids.map((h) => w.homes.find((x) => x.home_id === h)).filter((h): h is MockWorld['homes'][number] => !!h);
      const pv: Preview = {
        preview_id: `ktv-MOCK${++seq}`,
        expires_at: iso(now + 600_000),
        template_id: tp.t.template_id,
        version: v,
        template_org_id: org,
        homes: homes.map((h, i) => {
          const rows = rowsFor(tp.versions[v - 1], i === 0, (h.org_id ?? org) !== org);
          return { home_id: h.home_id, org_id: h.org_id ?? org, rows, pending_rules: rows.filter((r) => r.pending_rule).length, changes: rows.filter((r) => r.change !== 'same' && !r.customer_field).length };
        }),
        skipped: b.home_ids.filter((h) => !homes.some((x) => x.home_id === h)).map((h) => ({ home_id: h, reason: 'no_permission' as const })),
        pending_rules_by_org: [],
        names: { homes: Object.fromEntries(w.homes.map((h) => [h.home_id, h.display_name])), orgs: { ...Object.fromEntries(w.orgs.map((o) => [o.org_id, o.display_name])), 'org-mock-customer': '晴光文具' }, users: { 'u-mock-customer': '劉建宏' } },
      };
      const by = new Map<string, number>();
      for (const h of pv.homes) if (h.pending_rules) by.set(h.org_id, (by.get(h.org_id) ?? 0) + h.pending_rules);
      pv.pending_rules_by_org = [...by.entries()].map(([org_id, count]) => ({ org_id, count }));
      return pv;
    }
    if (sub === 'apply') {
      const b = body as { home_ids: string[]; version: number; preview_id: string };
      const job: TemplateJob & { startMs: number } = {
        job_id: `ktj-MOCK${++seq}`,
        template_id: tp.t.template_id,
        version: b.version || tp.t.current_version,
        preview_id: b.preview_id,
        state: 'running',
        cancel_requested: false,
        total: b.home_ids.length,
        counts: { pending: b.home_ids.length, applied: 0, skipped: 0, failed: 0 },
        created_by: w.me,
        created_at: iso(now),
        started_at: iso(now),
        notified_orgs: [],
        homes: b.home_ids.map((h) => ({ home_id: h, org_id: w.homes.find((x) => x.home_id === h)?.org_id ?? org, result: 'pending', changed_items: [], pending_rules: 0, changed_since_preview: false })),
        startMs: now,
      };
      jobs.push(job);
      for (const h of b.home_ids) applied[h] = { template_id: tp.t.template_id, version: job.version };
      return { job_id: job.job_id, preview_id: b.preview_id, created: true };
    }
    if (sub === 'apply-jobs') {
      if (!subId) return { template_id: tp.t.template_id, jobs: summaries() };
      const j = jobs.find((x) => x.job_id === decodeURIComponent(subId));
      if (!j) throw notFound();
      if (verb === 'cancel') Object.assign(j, { cancel_requested: true, state: 'cancelled', finished_at: iso(now) });
      return { ...j, names: { homes: Object.fromEntries(w.homes.map((h) => [h.home_id, h.display_name])), orgs: Object.fromEntries(w.orgs.map((o) => [o.org_id, o.display_name])), users: {} } };
    }
    throw notFound();
  },
};
